import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const SnCFoodTypes = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [foodTypeCode, setFoodTypeCode] = useState<string | null>(null);
  const [foodTypeName, setFoodTypeName] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeFieldError, setCodeFieldError] = useState("");
  const [nameFieldError, setNameFieldError] = useState("");

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setCodeFieldError("");
    setNameFieldError("");
    const codeLabel = "Food Type Code";
    const nameLabel = "Food Type Name";
    if (!foodTypeCode) {
      setCodeFieldError(codeLabel + validationMessages.sncMandatorySuffix);
    }
    if (!foodTypeName) {
      setNameFieldError(nameLabel + validationMessages.sncMandatorySuffix);
    }
    if (!foodTypeCode || !foodTypeName) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        foodTypeCode,
        validationLength.originCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(validationMessages.originCode);
      return false;
    }
    if (
      !validateString(
        foodTypeName,
        validationLength.originName,
        validationType.OTHER,
      )
    ) {
      setNameFieldError(validationMessages.originName);
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, "Food Type Code", foodTypeCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.foodTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], "Food Type Code", foodTypeCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeFieldError("");
      setNameFieldError("");
      setFoodTypeCode(list["Food Type Code"] ?? null);
      setFoodTypeName(list["Food Type Name"] ?? null);
    } else {
      setFoodTypeCode(null);
      setFoodTypeName(null);
      setCodeFieldError("");
      setNameFieldError("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  const payload = {
    "Food Type Code": foodTypeCode,
    "Food Type Name": foodTypeName,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Food Types
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders?.categoryGroup ?? "Food Types",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack direction="column">
            <FormLabel component="legend">
              Food Type Code
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              placeholder="Food Type Code"
              value={foodTypeCode ?? ""}
              error={!!codeFieldError}
              helperText={codeFieldError}
              onChange={(e) => {
                setFoodTypeCode(e.target.value || null);
                setCodeFieldError("");
              }}
            />
          </Stack>
          <Stack direction="column">
            <FormLabel component="legend">
              Food Type Name*
              <Tooltip title={tooltipComponent(golfTooltipHeaders.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>{" "}
            <TextField
              required
              variant="outlined"
              size="small"
              placeholder="Food Type Name"
              value={foodTypeName ?? ""}
              error={!!nameFieldError}
              helperText={nameFieldError}
              onChange={(e) => {
                setFoodTypeName(e.target.value || null);
                setNameFieldError("");
              }}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Food Type
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Food Types Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Food Type Code"
              name="Food Type Name"
            />
          ) : (
            <Typography variant="body2">No Food Types Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCFoodTypes;
